"use client";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import useMeasure from "react-use-measure";

interface Props {}

const BASE_SPACING = 32;
const MAX_COLUMNS = 100;
const MAX_ROWS = 100;
const MAX_RADIUS = 200;
const MIN_SCALE = 0.5;
const MAX_RADIUS_SQ = MAX_RADIUS * MAX_RADIUS;
const CHAR_GRADIENT =
    "$@ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_+~<>·";
const GRADIENT_CHARS = CHAR_GRADIENT.split("");
const TICK_INTERVAL = 60; // in milliseconds

const CHAR_POOL = GRADIENT_CHARS.filter((char) => char !== "·");

type Pointer = { x: number; y: number } | null;

const InteractiveBackground = (props: Props) => {
    const [measureRef, bounds] = useMeasure();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const setContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            containerRef.current = node;
            measureRef(node);
        },
        [measureRef]
    );
    const [pointer, setPointer] = useState<Pointer>(null);
    const pointerRef = useRef<Pointer>(null);
    const rafId = useRef<number | null>(null);
    const [characterMap, setCharacterMap] = useState<string[]>([]);

    const width = bounds.width || 0;
    const height = bounds.height || 0;

    const { columns, spacing } = useMemo(() => {
        if (width <= 0) {
            return { columns: 0, spacing: BASE_SPACING };
        }

        const limitedColumns = Math.min(
            MAX_COLUMNS,
            Math.max(1, Math.round(width / BASE_SPACING))
        );

        return {
            columns: limitedColumns,
            spacing: width / (limitedColumns + 1),
        };
    }, [width]);

    const xPositions = useMemo(() => {
        if (!columns || spacing <= 0) {
            return [];
        }

        return Array.from({ length: columns }, (_, col) => spacing * (col + 1));
    }, [columns, spacing]);

    const yPositions = useMemo(() => {
        if (height <= 0 || spacing <= 0) {
            return [];
        }

        const rows = Math.min(
            MAX_ROWS,
            Math.max(1, Math.floor(height / spacing) + 1)
        );

        return Array.from({ length: rows }, (_, row) => spacing * row);
    }, [height, spacing]);

    const dots = useMemo(() => {
        if (!xPositions.length || !yPositions.length) {
            return [];
        }

        return yPositions.flatMap((top) =>
            xPositions.map((left) => ({ top, left }))
        );
    }, [xPositions, yPositions]);

    const totalDots = dots.length;

    const randomCharacter = useCallback(() => {
        const index = Math.floor(Math.random() * CHAR_POOL.length);

        return CHAR_POOL[index] ?? "·";
    }, []);

    useEffect(() => {
        if (!totalDots) {
            setCharacterMap([]);
            return;
        }

        setCharacterMap((prev) => {
            if (prev.length === totalDots) {
                return prev;
            }

            const next = prev.slice(0, totalDots);

            while (next.length < totalDots) {
                next.push(randomCharacter());
            }

            return next;
        });
    }, [randomCharacter, totalDots]);

    const schedulePointerUpdate = useCallback(() => {
        if (typeof window === "undefined") {
            setPointer(pointerRef.current);
            return;
        }

        if (rafId.current !== null) {
            return;
        }

        rafId.current = window.requestAnimationFrame(() => {
            rafId.current = null;
            const next = pointerRef.current;

            setPointer((prev) => {
                if (
                    prev &&
                    next &&
                    Math.abs(prev.x - next.x) < 0.5 &&
                    Math.abs(prev.y - next.y) < 0.5
                ) {
                    return prev;
                }

                if (!prev && !next) {
                    return prev;
                }

                return next;
            });
        });
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const handlePointerMove = (event: PointerEvent) => {
            const element = containerRef.current;

            if (!element) {
                return;
            }

            const rect = element.getBoundingClientRect();
            pointerRef.current = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            };

            schedulePointerUpdate();
        };

        const handlePointerLeaveWindow = (event: PointerEvent) => {
            if (event.relatedTarget) {
                return;
            }

            pointerRef.current = null;
            schedulePointerUpdate();
        };

        const handleTap = (event: TouchEvent) => {
            const element = containerRef.current;

            if (!element) {
                return;
            }

            const rect = element.getBoundingClientRect();
            pointerRef.current = {
                x: event.touches[0].clientX - rect.left,
                y: event.touches[0].clientY - rect.top,
            };

            schedulePointerUpdate();
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerleave", handlePointerLeaveWindow);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener(
                "pointerleave",
                handlePointerLeaveWindow
            );
            window.removeEventListener("touchstart", handleTap);
        };
    }, [schedulePointerUpdate]);

    useEffect(
        () => () => {
            if (rafId.current !== null && typeof window !== "undefined") {
                cancelAnimationFrame(rafId.current);
            }
        },
        []
    );

    useEffect(() => {
        if (typeof window === "undefined" || totalDots === 0) {
            return;
        }

        const interval = window.setInterval(() => {
            setCharacterMap((prev) => {
                if (!prev.length) {
                    return prev;
                }

                const updates = Math.max(1, Math.floor(prev.length * 0.7));
                const next = [...prev];

                for (let i = 0; i < updates; i += 1) {
                    const targetIndex = Math.floor(Math.random() * next.length);
                    next[targetIndex] = randomCharacter();
                }

                return next;
            });
        }, TICK_INTERVAL);

        return () => window.clearInterval(interval);
    }, [randomCharacter, totalDots]);

    return (
        <div
            className="relative w-full h-full flex-1 top-0 left-0 text-base -mt-4x -z-x -ml-1 select-none"
            ref={setContainerRef}
        >
            {dots.map(({ left, top }, index) => {
                if (!pointer) {
                    return (
                        <div
                            key={index}
                            className="absolute text-background-2"
                            style={{ left, top }}
                        >
                            &middot;
                        </div>
                    );
                }

                const dx = pointer.x - left;
                const dy = pointer.y - top;
                const distanceSq = dx * dx + dy * dy;

                if (distanceSq > MAX_RADIUS_SQ) {
                    return (
                        <div
                            key={index}
                            className="absolute text-background-2"
                            style={{ left, top }}
                        >
                            &middot;
                        </div>
                    );
                }

                const distance = Math.sqrt(distanceSq);
                const influence = 1 - Math.min(1, distance / MAX_RADIUS);
                const scale = MIN_SCALE + influence * (1 - MIN_SCALE);
                const char = characterMap[index] ?? "·";

                return (
                    <div
                        key={index}
                        className="absolute text-background-2 pointer-events-none select-none"
                        style={{
                            left,
                            top,
                            transform: `scale(${scale})`,
                            transformOrigin: "center",
                        }}
                    >
                        {char}
                    </div>
                );
            })}
        </div>
    );
};

export default InteractiveBackground;
